import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { normalizeUserPostLoginReturnPath } from '@/lib/auth/session-storage'
import { getStoredToken } from '@/lib/auth/token-storage'

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('user'),
  portal: 'user',
})
const publicClient = createApiClient({ baseUrl: runtimeConfig.apiBaseUrl })
const wecomAuthDestinationPattern = /^\/(?:apps|chat|profile)(?:\/[^?#]*)?(?:\?[^#]*)?$/

/**
 * 企微 Auth 只允许回到后端 contract 指定的普通用户页面。
 * 登录恢复页虽由共享 helper 接受，但不能成为 callback 的最终跳转目标。
 */
export function resolveWecomAuthDestination(value: string | null | undefined) {
  const candidate = value?.trim() ?? ''
  // bot Auth 只允许返回签名 token 对应的精确恢复页。
  if (/^\/im\/wecom\/bind\/[A-Za-z0-9_-]{32,128}\?wecom_auth=1$/.test(candidate)) {
    return candidate
  }
  if (normalizeUserPostLoginReturnPath(candidate)) {
    return '/apps'
  }
  if (!wecomAuthDestinationPattern.test(candidate)) {
    return '/apps'
  }
  let path: string
  try {
    path = decodeURIComponent(candidate.split('?')[0])
  } catch {
    return '/apps'
  }
  if (
    path.includes('\\') ||
    path.includes('%') ||
    Array.from(path).some((character) => character.charCodeAt(0) <= 32)
  )
    return '/apps'
  return path.split('/').some((part) => part === '.' || part === '..') ? '/apps' : candidate
}

export async function exchangeWecomAuthSession() {
  const response = await publicClient.request<
    SuccessEnvelope<{
      password_reset_required?: boolean
      refresh_token: string
      return_to: string
      token: string
      user: unknown
    }>
  >('/app/auth/wecom/exchange', {
    credentials: 'include',
    method: 'POST',
  })

  return response.data
}

export async function confirmWecomAuthBinding() {
  const response = await apiClient.request<SuccessEnvelope<{ return_to: string }>>(
    '/app/auth/wecom/confirm',
    {
      body: JSON.stringify({ confirmed: true }),
      credentials: 'include',
      method: 'POST',
    },
  )
  return response.data
}

export interface WecomAuthContext {
  phase: string
  transaction_version: number
  binding_mode: 'manual' | 'email_match'
  profile_display_name: string
  allowed_actions: ('authorize_email' | 'exchange' | 'login' | 'confirm' | 'restart')[]
  last_error_code: string | null
  return_to: string
  expires_at: string
}

/** 匿名 cookie-only context 决定所有认证动作，不使用 URL 中的身份或错误提示。 */
export async function getWecomAuthContext() {
  const response = await publicClient.request<SuccessEnvelope<WecomAuthContext>>(
    '/app/auth/wecom/context',
    { credentials: 'include' },
  )
  return response.data
}

/** 主动点击后只发送确认与版本，不允许前端指定目标账号或邮箱。 */
export async function startWecomEmailBinding(version: number) {
  const response = await publicClient.request<
    SuccessEnvelope<{ authorization_url: string; transaction_version: number }>
  >('/app/auth/wecom/email/start', {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify({ confirmed: true, expected_transaction_version: version }),
  })
  return response.data
}
